/**
 * Chat Context for managing conversation state.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { chatApi } from "../lib/api/chat";
import { useAuth } from "./AuthContext";
import type { ConversationCreateResponse } from "../types/chat";

interface ChatContextType {
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  createNewConversation: () => Promise<string>;
  clearConversation: () => void;
  isCreatingConversation: boolean;
  error: string | null;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { clerkUser } = useAuth();

  /**
   * Create a new conversation and return its ID.
   */
  const createNewConversation = async (): Promise<string> => {
    if (!clerkUser) {
      const errorMessage = "User not authenticated";
      setError(errorMessage);
      throw new Error(errorMessage);
    }

    setIsCreatingConversation(true);
    setError(null);

    try {
      const response: ConversationCreateResponse =
        await chatApi.createConversation();
      const newConversationId = response.conversation_id;

      setConversationId(newConversationId);

      console.log("Created new conversation:", newConversationId);
      return newConversationId;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create conversation";
      setError(errorMessage);
      console.error("Failed to create conversation:", err);
      throw err;
    } finally {
      setIsCreatingConversation(false);
    }
  };

  // No automatic conversation creation on mount
  // Conversations are created when the first message is sent or when navigating to an existing conversation

  /**
   * Clear conversation ID when starting a new chat session.
   * This should be called when the user explicitly wants to start a new conversation.
   */
  const clearConversation = () => {
    setConversationId(null);
  };

  const contextValue: ChatContextType = {
    conversationId,
    setConversationId,
    createNewConversation,
    clearConversation,
    isCreatingConversation,
    error,
  };

  return (
    <ChatContext.Provider value={contextValue}>{children}</ChatContext.Provider>
  );
}

/**
 * Hook to use the ChatContext.
 */
export function useChat(): ChatContextType {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error("useChat must be used within a ChatProvider");
  }
  return context;
}
